import React, { Fragment, useContext } from 'react';
import _ from 'lodash';
import classNames from 'classnames';
import { I18nProvider } from '@fiverr-private/fiverr_context';
import { DEFAULT_LOCALE } from '../../../../shared/constants';
import { includesAds } from '../../utils/index';
import { SearchPropTypes } from '../../types/index';
import { DynamicGigListings, ProFirstGigListings, Pagination, RecommendationsCarousels, SelectedFilters, EmptySearchResults, NumberOfResults, SortBy, TopBarFilters, MobileFilters, NoResults } from '../common';
import StickyComponent from '../hoc/stickyComponent';
import MachineTranslationButtonWrapper from '../common/machine_translation_button_wrapper';
import { isQueryInTilesTest } from '../../experiments/serviceTilesSearch/utils';
import { DOMINATE_SC_FOR_SEARCH } from '../../experiments/serviceTilesSearch/constants';
import { MINIMUN_TILES_LENGTH } from '../common/tiles_carousel/constants';
import ScTilesCarousel from '../common/sc_tiles_carousel';
import TopFiltersCarousel from '../common/top_filters_carousel';
import Modalities from '../common/modalities/Modalities';
import { AppContext } from '../context/listingsContext';
import { getFilterTilesCarousel } from '../common/top_filters_carousel/utils';
import { getScTilesData } from '../common/sc_tiles_carousel/utils';
import { LISTINGS_TYPES, TRACKING_NAMES } from '../../constants';
import getLogoMakerBanner from '../../config/logoMakerBanner';
import getWebsiteMatchBanner from '../../config/websiteMatchBanner';
import SearchResultsCounter from './search_results_counter';
import RelatedSearch from './related_search';
import SearchHeader from './search_header';
import GuidedSearchNav from './guided_search_nav';

const Search = (props) => {

    const {
        pagination,
        sorting,
        displayData,
        topBarSorting,
        loggedIn
    } = props;

    const {
        isTouch,
        listings: [listings],
        inUgcTranslationsRollout,
        inWebsiteMatchBannerTest,
        showLMBannerInSearchPage,
        dominateSubCategoryId,
        filter: appFilters,
        significantLeafCategories = [],
        inSearchServiceTiles,
        query
    } = useContext(AppContext);

    const { gigs, choiceModalities, proData, context: { categoryIds, sessionLocale, abTestsProps, pageCtxId }, showListViewCards } = listings;
    const { active, selected, topBar, sideBar, subCategory } = appFilters;
    const { inGuidedSearchNavExp, inPromoteMTCtaExpCGroup } = abTestsProps;
    const noGigs = !gigs.length > 0 && !proData;
    const isEmptySearchBase = proData ? noGigs && proData.proGigs === 0 : noGigs;
    const isEmptySearch = _.isEmpty(active) && isEmptySearchBase;
    const hasFilters = sideBar.length > 0;
    const selectedSorting = topBarSorting.find((sort) => sort.selected) || topBarSorting[0];
    const listingsWithAds = includesAds(listings.gigs) || (!!proData && includesAds(proData.proGigs));
    const scTilesData = getScTilesData(significantLeafCategories, subCategory);
    const subCatFiltersCarousel = getFilterTilesCarousel(dominateSubCategoryId);
    const shouldShowModalities = !_.isEmpty(choiceModalities) && !_.isEmpty(choiceModalities.buckets);
    const shouldShowFiltersCarousel = inSearchServiceTiles && isQueryInTilesTest(query) && !shouldShowModalities && DOMINATE_SC_FOR_SEARCH.includes(dominateSubCategoryId);
    const shouldShowScCarousel = !shouldShowFiltersCarousel && !shouldShowModalities && scTilesData.length >= MINIMUN_TILES_LENGTH && !dominateSubCategoryId;
    const shouldShowRelatedSearch = !inGuidedSearchNavExp && !shouldShowScCarousel && !shouldShowFiltersCarousel && sessionLocale === DEFAULT_LOCALE;
    let selectedScName = subCategory ? subCategory.alias : '';

    if (subCategory && !subCategory.selected) {
        const [item] = subCategory.children.filter((item) => item.selected);
        selectedScName = item ? item.alias : selectedScName;
    }

    const hasGuidedSearchTerms = displayData.guidedSearchNavData && displayData.guidedSearchNavData.length;
    const shouldRenderGuidedSearch = sessionLocale === DEFAULT_LOCALE && inGuidedSearchNavExp;
    const displayTranslationButton = sessionLocale !== DEFAULT_LOCALE && inUgcTranslationsRollout;
    const listingsType = LISTINGS_TYPES.SEARCH;

    const logoMakerBanner = getLogoMakerBanner({
        shouldShowBanner: showLMBannerInSearchPage,
        isTouch,
        showListViewCards,
        page: TRACKING_NAMES.SEARCH_PAGE,
        listingsType,
        query: displayData.searchComponentData.query
    });
    const websiteMatchBanner = !logoMakerBanner && getWebsiteMatchBanner({
        inWebsiteMatchBannerTest,
        listingsType,
        query,
        categoryIds
    });

    return (
        <div className={classNames('search-page', { 'top-filters': !isTouch })}>
            {!isEmptySearch ?
                <Fragment>
                    {isTouch && <Fragment>
                        {shouldRenderGuidedSearch && <GuidedSearchNav terms={displayData.guidedSearchNavData} query={displayData.searchComponentData.query} pageCtxId={pageCtxId}/>}

                        <I18nProvider>
                            {({ i18n }) => (
                                <SearchResultsCounter paginationData={pagination} displayData={displayData} i18n={i18n}/>
                            )}
                        </I18nProvider>
                    </Fragment>}

                    {shouldShowRelatedSearch &&
                    <RelatedSearch displayData={displayData}/>}
                    {isTouch && hasFilters &&
                        <>
                            <StickyComponent>
                                <MobileFilters activeFilters={active}
                                    sorts={topBarSorting}
                                    filters={sideBar}/>
                            </StickyComponent>
                            {displayTranslationButton &&
                            <MachineTranslationButtonWrapper gigsListings={[listings.gigs]}
                                showSeparator={false}/>}
                        </>
                    }

                    {!isTouch &&
                        <Fragment>
                            <div className="layout-row header-wrapper">
                                <SearchHeader subCategoryData={subCategory} searchData={displayData.searchComponentData} hasGuidedSearchTerms={hasGuidedSearchTerms} inGuidedSearchNavExp={inGuidedSearchNavExp}/>
                            </div>
                            {
                                shouldShowFiltersCarousel &&
                                <div className="layout-row">
                                    <TopFiltersCarousel topFiltersCarousel={subCatFiltersCarousel} subCategoryId={dominateSubCategoryId}/>
                                </div>
                            }
                            {
                                shouldShowScCarousel &&
                                <div className="layout-row">
                                    <ScTilesCarousel scTilesData={scTilesData}/>
                                </div>
                            }
                            {shouldRenderGuidedSearch && <GuidedSearchNav terms={displayData.guidedSearchNavData} query={displayData.searchComponentData.query} pageCtxId={pageCtxId}/>}
                            <TopBarFilters filters={topBar}
                                selectedFilters={selected}
                                activeFilters={active}
                                subCategory={subCategory}
                                noGigs={noGigs}
                                fromSearchFlow/>

                            <div className="selected-filters-wrapper layout-row">
                                <SelectedFilters selectedFilters={selected}
                                    activeFilters={active}
                                    selectedScName={selectedScName}/>
                            </div>

                            {!noGigs && <div className="layout-row top-secondary">
                                <div className={classNames('results-info', { 'flex-center': inPromoteMTCtaExpCGroup })}>
                                    <NumberOfResults pagination={pagination} withAds={listingsWithAds}/>
                                    {displayTranslationButton &&
                                        <MachineTranslationButtonWrapper gigsListings={[listings.gigs]}
                                            showSeparator/>}
                                </div>
                                <SortBy title={selectedSorting.alias}
                                    data={topBarSorting}/>
                            </div>}
                        </Fragment>}
                    { shouldShowModalities &&
                        <div className="layout-row modalities-row">
                            <Modalities listing={listings}/>
                        </div>
                    }
                    <div className="layout-row content-row">
                        {noGigs && <NoResults/>}
                        <div className="content">
                            {proData ?
                                <ProFirstGigListings listings={listings}/> :
                                <DynamicGigListings listings={listings}
                                    banners={[logoMakerBanner, websiteMatchBanner]}/>
                            }
                        </div>

                        {window && window.location && <Pagination pagination={pagination}/>}
                    </div>
                </Fragment> :
                <EmptySearchResults displayData={displayData} loggedIn={loggedIn}/>
            }
            {(!isTouch && loggedIn) && <RecommendationsCarousels sorting={sorting} activeFilters={active} loggedIn={loggedIn}/>}
        </div>
    );
};

Search.propTypes = SearchPropTypes;

export default Search;
